import { Box } from '@/ui/primitives';
import { r, s } from '../../theme/tokens';
import { LastUpdated } from './LastUpdated';
import type { SxProps, Theme } from '@/ui/primitives';
import type { ReactElement, RefObject } from 'react';

type ServerDataTableFrameProps = {
  containerStyles: Array<Exclude<SxProps<Theme>, readonly unknown[] | boolean | null | undefined>>;
  contentRef?: RefObject<HTMLDivElement | null>;
  dataError: string | null;
  dataState: string;
  dataTable: ReactElement | null;
  hasRows: boolean;
  isFetching: boolean;
  lastUpdatedAt: number | null;
  placeholder: ReactElement | null;
  resolvedHeight?: number;
  showLastUpdated: boolean;
  showPlaceholderGrid: boolean;
  showSkeletonGrid: boolean;
  skeleton: ReactElement | null;
  skeletonHeightAnchor: ReactElement | null;
};

export const ServerDataTableFrame = ({
  containerStyles,
  contentRef,
  dataError,
  dataState,
  dataTable,
  hasRows,
  isFetching,
  lastUpdatedAt,
  placeholder,
  resolvedHeight,
  showLastUpdated,
  showPlaceholderGrid,
  showSkeletonGrid,
  skeleton,
  skeletonHeightAnchor,
}: ServerDataTableFrameProps): ReactElement => {
  const renderedDataTable =
    dataTable === null ? null : <Box sx={{ width: '100%', minWidth: 0 }}>{dataTable}</Box>;
  const anchoredSkeleton =
    showSkeletonGrid && skeletonHeightAnchor !== null ? (
      <>
        <Box
          aria-hidden="true"
          data-table-height-anchor="true"
          sx={{
            left: 0,
            position: 'absolute',
            top: 0,
            width: '100%',
            minWidth: 0,
            pointerEvents: 'none',
            visibility: 'hidden',
          }}
        >
          {skeletonHeightAnchor}
        </Box>
        {skeleton}
      </>
    ) : null;
  const fallbackContent = showSkeletonGrid ? (
    hasRows ? (
      <>
        {renderedDataTable}
        <Box
          aria-hidden="true"
          sx={{
            left: 0,
            minWidth: 0,
            pointerEvents: 'none',
            position: 'absolute',
            top: 0,
            width: '100%',
          }}
        >
          {skeleton}
        </Box>
      </>
    ) : (
      skeleton
    )
  ) : showPlaceholderGrid ? (
    placeholder
  ) : (
    renderedDataTable
  );
  const frameContent = anchoredSkeleton ?? fallbackContent;

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <Box
        data-testid="grid-ready"
        data-state={dataState}
        data-error={dataError ?? ''}
        sx={{ display: 'none' }}
      />
      <Box
        ref={contentRef}
        data-state={dataState}
        data-fetching={isFetching ? 'true' : 'false'}
        sx={[
          {
            height: resolvedHeight ?? 'auto',
            width: '100%',
            minWidth: 0,
            backgroundColor: 'background.paper',
            borderRadius: r('none'),
            boxShadow: 'none',
            boxSizing: 'border-box',
            position: 'relative',
            overflow: 'hidden',
            overflowAnchor: 'none',
            '& .MuiDataGrid-root': { border: 'none' },
          },
          ...containerStyles,
        ]}
      >
        {frameContent}
      </Box>
      {showLastUpdated ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: s('sm'), pt: s('sm') }}>
          <LastUpdated timestamp={lastUpdatedAt} />
        </Box>
      ) : null}
    </Box>
  );
};
